import React, { useState, useEffect, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { IconButton } from '@mui/material';
import Swal from "sweetalert2";
import { Delete, Download } from '@mui/icons-material';
import "../../styles/MBABatch.css";
import FeesDetailsTable from "../Dashboard/Tables/FeesDetailsTable"; // Import your new table component
import NISMCertificationsTable from "../Dashboard/Tables/NISMCertificationsTable";
import DYPCollegeFeesTable from "../Dashboard/Tables/DYPCollegeFeesTable";
import FinxlCertificationsTable from "../Dashboard/Tables/FinxlCertificationsTable";
import PresentationsTable from "../Dashboard/Tables/PresentationsTable";
import { getMBABatchList, getPresentationsDetailsList, getNISMDetailsList, getFINXLDetailsList, getFeesDetailsList, getDYPCollegeFeesDetailsList } from '../../services/getAllApi';
import { saveMBABatchList } from '../../services/getAllApi';
import { deleteMBABatchList } from "../../services/getAdminApi"; // adjust the path as per your file structure



const MBABatch = () => {

  const [activeTable, setActiveTable] = useState(""); // Track which table is active

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);

  const [feesDetailsFormData, setFeesDetailsFormData] = useState({});
  const [feesDetailsLoading, setFeesDetailsLoading] = useState(false);
  const [feesDetailsError, setFeesDetailsError] = useState(null);

  const [dypCollegeFeesFormData, setDypCollegeFeesFormData] = useState({});
  const [dypCollegeFeesLoading, setDypCollegeFeesLoading] = useState(false);
  const [dypCollegeFeesError, setDypCollegeFeesError] = useState(null);

  const [nismFormData, setNismFormData] = useState({});
  const [nismLoading, setNismLoading] = useState(false);
  const [nismError, setNismError] = useState(null);

  const [finxlFormData, setFinxlFormData] = useState({});
  const [finxlLoading, setFinxlLoading] = useState(false);
  const [finxlError, setFinxlError] = useState(null);

  const [presentationsFormData, setPresentationsFormData] = useState({});
  const [presentationsLoading, setPresentationsLoading] = useState(false);
  const [presentationsError, setPresentationsError] = useState(null);

  // 1️⃣ Resizable Table Logic
  const [colWidths, setColWidths] = useState({
    srNo: 50,
    student_id: 90,
    name: 200, // Resizable
    mobile: 92,
    email: 200, // Resizable
    registrationDate: 80,
    course: 130,
    courseDuration: 130,
    mode: 100,
    counsellor: 150, // Resizable
    dySem: 100,
    comments: 200, // Resizable
    uniform: 100,
    applicationFees: 120,
    totalfeesPaid: 100,
    actions: 100,
  });

  // Mapping for column display names
  const colDisplayNames = {
    student_id: "Student ID",
    name: "Name",
    mobile: "Mobile",
    email: "Email",
    registrationDate: "Registration Date",
    course: "Course",
    courseDuration: "Course Duration",
    mode: "Mode",
    counsellor: "Counsellor",
    dySem: "DY Sem",
    comments: "Comments",
    uniform: "Uniform",
    applicationFees: "Application Fees",
    totalfeesPaid: "Total Fees Paid",
    actions: "Actions",
  };

  const resizingColumn = useRef(null);

  const handleMouseDown = (e, column) => {
    resizingColumn.current = { column, startX: e.clientX, startWidth: colWidths[column] };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!resizingColumn.current) return;
    const { column, startX, startWidth } = resizingColumn.current;
    const newWidth = Math.max(50, startWidth + (e.clientX - startX)); // Min width 50px
    setColWidths((prev) => ({ ...prev, [column]: newWidth }));
  };

  const handleMouseUp = () => {
    resizingColumn.current = null;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };



  const fetchStudents = async () => {
    setStudentsLoading(true);
    setStudentsError(null);
    try {
      const response = await getMBABatchList();
      const studentsData = response?.data || response; // handle both cases
      setStudents(studentsData);
    } catch (error) {
      setStudentsError(error.message || "Error fetching MBA batch list");
    } finally {
      setStudentsLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);


  // Fetch Fees Details data
  // Debounce helper
  const debounce = (func, delay) => {
    let timer;
    return (...args) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const fetchFeesDetailsDebounced = useCallback(
    debounce(async (studentsList) => {
      setFeesDetailsLoading(true);
      setFeesDetailsError(null);
      try {
        const feesResponse = await getFeesDetailsList();
        if (!feesResponse) throw new Error("No fees data received.");

        const initialData = {};
        studentsList.forEach((student) => {
          const studentFees = feesResponse.find((fee) => fee.student_id === student.student_id) || {};
          initialData[student.student_id] = studentFees;
        });
        setFeesDetailsFormData(initialData);
      } catch (error) {
        setFeesDetailsError(error.message || "Error fetching fees data");
      } finally {
        setFeesDetailsLoading(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (students.length > 0) fetchFeesDetailsDebounced(students);
  }, [students, fetchFeesDetailsDebounced]);

  // Fetch DYP College Fees data
  const fetchDYPFeesDetailsDebounced = useCallback(
    debounce(async (studentsList) => {
      setDypCollegeFeesLoading(true);
      setDypCollegeFeesError(null);
      try {
        const dypFeesResponse = await getDYPCollegeFeesDetailsList();
        if (!dypFeesResponse) throw new Error("No DYP fees data received.");

        const initialData = {};
        studentsList.forEach((student) => {
          const dypCollegeFees = dypFeesResponse.find((fee) => fee.student_id === student.student_id) || {};
          initialData[student.student_id] = dypCollegeFees;
        });
        setDypCollegeFeesFormData(initialData);
      } catch (error) {
        setDypCollegeFeesError(error.message || "Error fetching DYP fees data");
      } finally {
        setDypCollegeFeesLoading(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (students.length > 0) fetchDYPFeesDetailsDebounced(students);
  }, [students, fetchDYPFeesDetailsDebounced]);
  // Function to add a new row
  const handleAddRow = () => {
    const newStudent = {
      id: students.length + 1,
      student_id: "",
      name: "",
      mobile: "",
      email: "",
      registrationDate: "",
      course: "",
      courseDuration: "",
      mode: "",
      counsellor: "",
      dySem: "",
      comments: "",
      uniform: "",
      applicationFees: "",
      totalfeesPaid: "",

      // isNew: true,
    };
    setStudents([...students, newStudent]);
  };

  // Fetch NISM data
  const fetchNISMDetailsDebounced = useCallback(
    debounce(async (studentsList) => {
      setNismLoading(true);
      setNismError(null);
      try {
        const nismresponse = await getNISMDetailsList();
        if (!nismresponse) throw new Error("No NISM certification data received.");

        const initialData = {};
        studentsList.forEach((student) => {
          const nismCertifications = nismresponse.find((nismcertification) => nismcertification.student_id === student.student_id) || {};
          initialData[student.student_id] = nismCertifications;
        });
        setNismFormData(initialData);
      } catch (error) {
        setNismError(error.message || "Error fetching NISM certification data");
      } finally {
        setNismLoading(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (students.length > 0) fetchNISMDetailsDebounced(students);
  }, [students, fetchNISMDetailsDebounced]);

  // Fetch FINXL data
  const fetchFINXLDetailsDebounced = useCallback(
    debounce(async (studentsList) => {
      setFinxlLoading(true);
      setFinxlError(null);
      try {
        const finxlresponse = await getFINXLDetailsList();
        if (!finxlresponse) throw new Error("No FINXL certification data received.");

        const initialData = {};
        studentsList.forEach((student) => {
          const finxlCertifications = finxlresponse.find((finxlcertification) => finxlcertification.student_id === student.student_id) || {};
          initialData[student.student_id] = finxlCertifications;
        });
        setFinxlFormData(initialData);
      } catch (error) {
        setFinxlError(error.message || "Error fetching FINXL certification data");
      } finally {
        setFinxlLoading(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (students.length > 0) fetchFINXLDetailsDebounced(students);
  }, [students, fetchFINXLDetailsDebounced]);

  // Fetch Presentations data
  const fetchPresentationsDetailsDebounced = useCallback(
    debounce(async (studentsList) => {
      setPresentationsLoading(true);
      setPresentationsError(null);
      try {
        const presentationresponse = await getPresentationsDetailsList();
        if (!presentationresponse) throw new Error("No Presentation Details data received.");

        const initialData = {};
        studentsList.forEach((student) => {
          const presentationsdata = presentationresponse.find((presentationtable) => presentationtable.student_id === student.student_id) || {};
          initialData[student.student_id] = presentationsdata;
        });
        setPresentationsFormData(initialData);
      } catch (error) {
        setPresentationsError(error.message || "Error fetching Presentation data");
      } finally {
        setPresentationsLoading(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    if (students.length > 0) fetchPresentationsDetailsDebounced(students);
  }, [students, fetchPresentationsDetailsDebounced]);

  // Function to edit cell values
  const handleEdit = (index, field, value) => {
    setStudents(prevStudents => {
      const updatedStudents = [...prevStudents];
      updatedStudents[index] = { ...updatedStudents[index], [field]: value };

      // Also update feesDetailsFormData for name and mobile fields to keep in sync
      if (field === "name" || field === "mobile") {
        setFeesDetailsFormData(prevFeesData => {
          const studentId = updatedStudents[index].student_id;
          return {
            ...prevFeesData,
            [studentId]: {
              ...prevFeesData[studentId],
              [field]: value,
            },
          };
        });
      }

      return updatedStudents;
    });
  };

  const [isSaving, setIsSaving] = useState(false); // Track saving state
  const [isDownloading, setIsDownloading] = useState(false);
  const [lastSavedData, setLastSavedData] = useState([]);


  let saveTimeout = null;

  const handleSave = async () => {
    if (isSaving || students.length === 0) return;

    if (saveTimeout) {
      Swal.fire("Info", "Please wait before saving again.", "info");
      return;
    }

    const isDuplicateSave = JSON.stringify(students) === JSON.stringify(lastSavedData);
    if (isDuplicateSave) {
      Swal.fire("Info", "No changes detected!", "info");
      return;
    }

    // Validation: Check required fields for each student
    for (const student of students) {
      if (!student.student_id || student.student_id.toString().trim() === "") {
        Swal.fire("Validation Error", "Student ID is required for all students.", "warning");
        return;
      }
      if (!student.name || student.name.trim() === "") {
        Swal.fire("Validation Error", "Name is required for all students.", "warning");
        return;
      }
      if (!student.email || student.email.trim() === "") {
        Swal.fire("Validation Error", "Email is required for all students.", "warning");
        return;
      }
      // Add more validation rules as needed
    }

    setIsSaving(true);

    try {
      const uniqueStudents = [...new Map(students.map(student => [student.student_id, student])).values()];

      console.log("Saving MBA Batch List with data:", uniqueStudents);

      const response = await saveMBABatchList(uniqueStudents);

      Swal.fire("Success", "Data saved successfully!", "success");
      setLastSavedData(uniqueStudents); // Update last saved data

      // Fetch updated data from backend to ensure frontend shows latest data
      await fetchStudents();

      // Set debounce timeout to prevent rapid saves
      saveTimeout = setTimeout(() => {
        saveTimeout = null;
      }, 2000);

    } catch (error) {
      console.error("Error saving data:", error);
      Swal.fire("Error", error.response?.data?.message || error.message || "Something went wrong!", "error");
    } finally {
      setIsSaving(false);
    }
  };



  const handleDownload = () => {
    if (!students || students.length === 0) {
      Swal.fire("Info", "No data to export!", "info");
      return;
    }

    // Helper function to map keys to display names and build array of objects for export
    const mapDataForExport = (dataArray, displayNames) => {
      return dataArray.map(item => {
        const mappedItem = {};
        Object.keys(displayNames).forEach(key => {
          if (key === "srNo") {
            // For srNo, use index + 1 if available
            mappedItem[displayNames[key]] = dataArray.indexOf(item) + 1;
          } else {
            mappedItem[displayNames[key]] = item[key] !== undefined ? item[key] : "";
          }
        });
        return mappedItem;
      });
    };

    // Modified flattenData to preserve order of students array
    const flattenData = (studentsArray, dataObj) => {
      return studentsArray.map(student => {
        return {
          student_id: student.student_id || student.id || "",
          ...dataObj[student.student_id || student.id] || {},
        };
      });
    };

    // Prepare MBA Students data
    const studentsExportData = mapDataForExport(students, colDisplayNames);

    // Prepare Fees Details data
    const feesDataArray = flattenData(students, feesDetailsFormData);
    const dypFeesDataArray = flattenData(students, dypCollegeFeesFormData);
    const nismDataArray = flattenData(students, nismFormData);
    const finxlDataArray = flattenData(students, finxlFormData);
    const presentationsDataArray = flattenData(students, presentationsFormData);

    // For other tables, create display name mappings (assuming similar keys)
    // For simplicity, use keys as headers for other tables (can be customized if needed)
    const feesDisplayNames = {
      student_id: "Student ID",
      name: "Name",
      mobile: "Mobile",
      application_fees: "Application Fees",
      application_fees_date: "Application Fees Date",
      application_fees_paid: "Application Fees Paid",
      total_fees: "Total Fees",
      total_fees_date: "Total Fees Date",
      total_fees_paid: "Total Fees Paid",
      first_installment: "1st Installment",
      first_install_date: "1st Install Date",
      first_install_paid: "1st Install Paid",
      second_installment: "2nd Installment",
      second_install_date: "2nd Install Date",
      second_install_paid: "2nd Install Paid",
      third_installment: "3rd Installment",
      third_install_date: "3rd Install Date",
      third_install_paid: "3rd Install Paid",
      balance_fees: "Balance Fees",
      balance_fees_date: "Balance Fees Date",
      balance_fees_paid: "Balance Fees Paid",
    };

    const dypFeesDisplayNames = {
      student_id: "Student ID",
      name: "Name",
      mobile: "Mobile",
      total_fees: "Total Fees",
      total_fees_date: "Total Fees Date",
      total_fees_paid: "Total Fees Paid",
      sem1_fees: "Sem1 Fees",
      sem1_fees_date: "Sem1 Fees Date",
      sem1_fees_paid: "Sem1 Fees Paid",
      sem2_fees: "Sem2 Fees",
      sem2_fees_date: "Sem2 Fees Date",
      sem2_fees_paid: "Sem2 Fees Paid",
      sem3_fees: "Sem3 Fees",
      sem3_fees_date: "Sem3 Fees Date",
      sem3_fees_paid: "Sem3 Fees Paid",
      sem4_fees: "Sem4 Fees",
      sem4_fees_date: "Sem4 Fees Date",
      sem4_fees_paid: "Sem4 Fees Paid",
      balance_fees: "Balance Fees",
      balance_fees_date: "Balance Fees Date",
      balance_fees_paid: "Balance Fees Paid",
    };

    const nismDisplayNames = {
      student_id: "Student ID",
      name: "Name",
      nismEDC: "Equity Derivatives",
      nismEDC_date: "Equity Derivatives Date",
      nismEDC_status: "Equity Derivatives Status",
      nismCDC: "Currency Derivatives",
      nismCDC_date: "Currency Derivatives Date",
      nismCDC_status: "Currency Derivatives Status",
      nismFSM: "Security Markets",
      nismFSM_date: "Security Markets Date",
      nismFSM_status: "Security Markets Status",
      nismRAC: "Research Analyst",
      nismRAC_date: "Research Analyst Date",
      nismRAC_status: "Research Analyst Status",
      nismMBC: "Merchant Banking",
      nismMBC_date: "Merchant Banking Date",
      nismMBC_status: "Merchant Banking Status",
      nismAIFMC: "Alt. Fund Manager",
      nismAIFMC_date: "Alt. Fund Manager Date",
      nismAIFMC_status: "Alt. Fund Manager Status",
      nismFISC: "Fixed Income",
      nismFISC_date: "Fixed Income Date",
      nismFISC_status: "Fixed Income Status",
    };

    const finxlDisplayNames= {
      student_id: "Student ID",
      name: "Name",
      fmer: "FMER",
      fmer_date: "FMER Date",
      fmer_status: "FMER Status",
      fpa: "FPA",
      fpa_date: "FPA Date",
      fpa_status: "FPA Status",
      ib: "IB",
      ib_date: "IB Date",
      ib_status: "IB Status",
      startupVal: "Startup Valuation",
      startupVal_date: "Startup Valuation Date",
      startupVal_status: "Startup Valuation Status",
      powerBi: "Microsoft Power BI",
      powerBi_date: "Microsoft Power BI Date",
      powerBi_status: "Microsoft Power BI Status",
      anaplan1: "Anaplan Level I",
      anaplan1_date: "Anaplan Level I Date",
      anaplan1_status: "Anaplan Level I Status",
      anaplan2: "Anaplan Level II",
      anaplan2_date: "Anaplan Level II Date",
      anaplan2_status: "Anaplan Level II Status",
    };

    const presentationsDisplayNames = {
      student_id: "Student ID",
      name: "Name",
      presentation1_date: "Presentation1 Date",
      presentation1_status: "Presentation1 Status",
      presentation2_date: "Presentation2 Date",
      presentation2_status: "Presentation2 Status",
      presentation3_date: "Presentation3 Date",
      presentation3_status: "Presentation3 Status",
      presentation4_date: "Presentation4 Date",
      presentation4_status: "Presentation4 Status",
      presentation5_date: "Presentation5 Date",
      presentation5_status: "Presentation5 Status",
      presentation6_date: "Presentation6 Date",
      presentation6_status: "Presentation6 Status",
    };

    // Map other tables data for export
    const feesExportData = mapDataForExport(feesDataArray, feesDisplayNames);
    const dypFeesExportData = mapDataForExport(dypFeesDataArray, dypFeesDisplayNames);
    const nismExportData = mapDataForExport(nismDataArray, nismDisplayNames);
    const finxlExportData = mapDataForExport(finxlDataArray, finxlDisplayNames);
    const presentationsExportData = mapDataForExport(presentationsDataArray, presentationsDisplayNames);

    // Create sheets
    const wsStudents = XLSX.utils.json_to_sheet(studentsExportData);
    const wsFees = XLSX.utils.json_to_sheet(feesExportData);
    const wsDYPFees = XLSX.utils.json_to_sheet(dypFeesExportData);
    const wsNismCert = XLSX.utils.json_to_sheet(nismExportData);
    const wsFinxlCert = XLSX.utils.json_to_sheet(finxlExportData);
    const wsPresentations = XLSX.utils.json_to_sheet(presentationsExportData);

    // Create workbook and append sheets
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsStudents, "MBA Students");
    XLSX.utils.book_append_sheet(wb, wsFees, "Fees Details");
    XLSX.utils.book_append_sheet(wb, wsDYPFees, "DYP Fees Details");
    XLSX.utils.book_append_sheet(wb, wsNismCert, "NISM Certification Details");
    XLSX.utils.book_append_sheet(wb, wsFinxlCert, "FINXL Certification Details");
    XLSX.utils.book_append_sheet(wb, wsPresentations, "Presentations Details");

    // Write file
    XLSX.writeFile(wb, "MBA_Batch_All_Data.xlsx");
  };


  // Function to delete a row
  const handleDelete = (index, student_id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // Call the API to delete the student from the backend
          const deletedStudent = await deleteMBABatchList(student_id);

          // If deletion is successful, update the UI
          if (deletedStudent) {
            setStudents((prev) => prev.filter((_, i) => i !== index)); // Remove from state
            Swal.fire('Deleted!', 'The student has been removed.', 'success');
          }
        } catch (error) {
          Swal.fire('Error!', 'Failed to delete the student.', 'error');
        }
      }
    });
  };




  return (
    <div>
      <div className="mba-heading-div">
        <p>MBA Students Data</p>
      </div>
      <div className="table-btns">
        {activeTable === "" && (
          <>
            <button onClick={handleAddRow} className="add-row-btn">
              <i className="bx bx-plus"></i>
              <p className="button-add">Add Row</p>
            </button>
            <button onClick={handleSave} disabled={isSaving} className="save-btn">
              <p className="button-save">{isSaving ? "Saving..." : "Save"} Data</p>
            </button>
            <button onClick={handleDownload} className="download-btn">
              {isDownloading ? (
                <p className="button-download">Downloading...</p>
              ) : (
                <Download className="button-download" />
              )}
            </button>


          </>
        )}
        <button onClick={() => setActiveTable("")} className="Click-btn"><p className={`button-list${activeTable === "" ? " active" : ""}`}> MBA Students List</p></button>
        <button onClick={() => setActiveTable("fees")} className="Click-btn"><p className={`button-fees${activeTable === "fees" ? " active" : ""}`}> Fees Details</p></button>
        <button onClick={() => setActiveTable("dypfeestable")} className="Click-btn"><p className={`button-DYPfees${activeTable === "dypfeestable" ? " active" : ""}`}> DYP College Fees Details</p></button>
        <button onClick={() => setActiveTable("nism")} className="Click-btn"><p className={`button-nism${activeTable === "nism" ? " active" : ""}`}> NISM Certifications</p></button>
        <button onClick={() => setActiveTable("finxlcert")} className="Click-btn"><p className={`button-finxl${activeTable === "finxlcert" ? " active" : ""}`}> Finxl Certifications</p></button>
        <button onClick={() => setActiveTable("presentations")} className="Click-btn"><p className={`button-presentation${activeTable === "presentations" ? " active" : ""}`}>Presentations Details</p></button>

      </div>

      {activeTable === "fees" && (
        <>

          <FeesDetailsTable
            students={students}
            isActive={activeTable === "fees"}
            handleSave={() => console.log("Save Clicked")}
            handleAddRow={() => console.log("Add Row Clicked")}
            isSaving={isSaving}
            onFeesDataChange={setFeesDetailsFormData}
          />
        </>
      )}
      {activeTable === "dypfeestable" && (
        <>

          <DYPCollegeFeesTable
            students={students}
            isActive={activeTable === "dypfeestable"}
            handleSave={() => console.log("Save Clicked")}
            handleAddRow={() => console.log("Add Row Clicked")}
            isSaving={isSaving}
            onDYPFeesDataChange={setDypCollegeFeesFormData}
          />
        </>
      )}
      {activeTable === "nism" && (
        <>

          <NISMCertificationsTable
            students={students}
            isActive={activeTable === "nism"}
            handleSave={() => console.log("Save Clicked")}
            handleAddRow={() => console.log("Add Row Clicked")}
            isSaving={isSaving}
            onNISMCertDataChange={setNismFormData}
          />
        </>
      )}
      {activeTable === "finxlcert" && (
        <>

          <FinxlCertificationsTable
            students={students}
            isActive={activeTable === "finxlcert"}
            handleSave={() => console.log("Save Clicked")}
            handleAddRow={() => console.log("Add Row Clicked")}
            isSaving={isSaving}
            onFinxlCertDataChange={setFinxlFormData}
          />
        </>
      )}
      {activeTable === "presentations" && (
        <>

          <PresentationsTable
            students={students}
            isActive={activeTable === "presentations"}
            handleSave={() => console.log("Save Clicked")}
            handleAddRow={() => console.log("Add Row Clicked")}
            isSaving={isSaving}
            onPresentationsDataChange={setPresentationsFormData}
          />
        </>
      )}

      <div style={{ display: activeTable ? "none" : "table" }}>
        <div className="table-container-MBA-Students-list1">
          <table>
            <thead>
              <tr>
                {Object.keys(colWidths).map((key) => (
                  <th key={key} style={{ width: colWidths[key], position: "relative" }}>
                    {colDisplayNames[key] || (key.charAt(0).toUpperCase() + key.slice(1).toLowerCase())}
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: "2px",
                        cursor: "col-resize",
                        background: "#d5e1ff85",
                      }}
                      onMouseDown={(e) => handleMouseDown(e, key)}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.isArray(students) && students.map((student, index) => (
                <tr key={student.id}>
                  <td>{index + 1}</td>

                  {/* Editable Cells */}
                  {Object.keys(student).map((key) =>
                    key !== "id" ? (
                      <td key={key} style={{ width: colWidths[key], padding: "5px" }}>
                        {key === "mode" || key === "dySem" ? (
                          <select
                            value={student[key]}
                            onChange={(e) => handleEdit(index, key, e.target.value)}
                            style={{ width: "100%" }}
                          >
                            <option value="">Select</option>
                            {key === "mode" ? (
                              <>
                                <option value="Online">Online</option>
                                <option value="Offline">Offline</option>
                                <option value="Hybrid">Hybrid</option>
                              </>
                            ) : (
                              <>
                                <option value="Sem 1">Semester 1</option>
                                <option value="Sem 2">Semester 2</option>
                                <option value="Sem 3">Semester 3</option>
                                <option value="Sem 4">Semester 4</option>
                              </>
                            )}
                          </select>
                        ) : (
                          <input
                            type={key.includes("Date") ? "date" : "text"}
                            value={student[key]}
                            onChange={(e) => handleEdit(index, key, e.target.value)}
                            style={{ width: "90%", padding: "5px" }}
                          />
                        )}
                      </td>
                    ) : null
                  )}

                  {/* Delete Button */}
                  <td>
                    <IconButton color="error" onClick={() => handleDelete(index, student.student_id)}>
                      <Delete />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>


          </table>
        </div>
      </div >
    </div >
  );
};

export default MBABatch;